import { FocusEventHandler, useEffect } from 'react';
import ReactCodeEditor from '@uiw/react-textarea-code-editor';
import { format } from 'sql-formatter';
import toast from 'react-hot-toast';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Button } from '@antimatterhq/ui/components/button';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { cn } from '@antimatterhq/ui/lib/utils';

export type CodeEditorProps = {
  code: string;
  language?: string;
  readOnly?: boolean;
  className?: string;
  placeHolder?: string;
  onChange?: (value: string) => void;
  name?: string;
  formatOptions?: {
    uppercase?: boolean;
    indent?: string;
    linesBetweenQueries?: number;
  };
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  hideControls?: boolean;
  'data-testid'?: string;
};

const defaultFormatOptions = {
  uppercase: true,
  indent: '  ',
  linesBetweenQueries: 2,
};

const buttonBaseClass = 'opacity-30 hover:opacity-100 transition-opacity px-1';

export const CodeEditor = ({
  code,
  placeHolder = '',
  language = 'sql',
  readOnly = false,
  className,
  onChange,
  name,
  formatOptions = defaultFormatOptions,
  onBlur,
  hideControls = false,
  'data-testid': dataTestId,
}: CodeEditorProps) => {
  const { isDarkMode } = useColorMode();

  useEffect(() => {
    const editor = document.querySelector('.w-tc-editor');
    if (editor) {
      editor.setAttribute('data-color-mode', isDarkMode ? 'dark' : 'light');
    }
  }, [isDarkMode]);

  const editorClass = cn(
    'w-full rounded-lg border p-4 text-sm',
    isDarkMode ? 'bg-grey-900 border-grey-700' : 'border-grey-200 bg-white',
    className,
    !code ? undefined : '!font-mono',
  );

  const formatSQL = (str: string) => {
    return format(str, formatOptions);
  };

  const handleFormat = () => {
    if (language === 'sql') {
      onChange?.(formatSQL(code));
      toast.success('SQL formatted successfully');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      onChange?.(text);
      toast.success('Content pasted successfully');
    } catch {
      toast.error('Failed to paste content');
    }
  };

  return (
    <>
      <ReactCodeEditor
        value={code}
        language={language}
        readOnly={readOnly}
        placeholder={placeHolder}
        padding={10}
        className={editorClass}
        data-color-mode={isDarkMode ? 'dark' : 'light'}
        onChange={(e) => onChange?.(e.target.value)}
        name={name}
        data-testid={dataTestId}
        onBlur={onBlur}
      />

      {!hideControls && (
        <div className="mt-2 flex justify-end gap-0">
          <Tooltip content="Copy to clipboard" side="bottom">
            <Button
              variant="ghost"
              size="small"
              onClick={handleCopy}
              className={buttonBaseClass}
              data-testid="code-editor-copy-button"
            >
              <Icon icon="lucide:copy" />
            </Button>
          </Tooltip>

          {!readOnly && (
            <>
              <Tooltip content="Paste from clipboard" side="bottom">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={handlePaste}
                  className={buttonBaseClass}
                  data-testid="code-editor-paste-button"
                >
                  <Icon icon="lucide:clipboard-paste" />
                </Button>
              </Tooltip>

              {language === 'sql' && (
                <Tooltip content="Format SQL" side="bottom">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleFormat}
                    className={buttonBaseClass}
                    data-testid="code-editor-format-button"
                  >
                    <Icon icon="icon-park-outline:format-brush" />
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};
